import React, { useState, useMemo } from 'react';
import { useTestCatalog } from '@/hooks/use-test-catalog'; // Assuming hook path
import TestCategoryList from '@/components/test-catalog/TestCategoryList';
import TestSearch from '@/components/test-catalog/TestSearch';
import TestDetailCard from '@/components/test-catalog/TestDetailCard';
import Loading from '@/components/ui/Loading';
import ErrorMessage from '@/components/ui/ErrorMessage';
import { EmptyState } from '@/components/ui/EmptyState';
import { useNavigate } from 'react-router-dom'; // For potential Order button

const TestCatalog: React.FC = () => {
  const { categories, tests, isLoading, error } = useTestCatalog();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const filteredTests = useMemo(() => {
    return tests
      .filter(test => 
        (!selectedCategory || test.category === selectedCategory)
      )
      .filter(test => 
        !searchTerm || 
        test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (test.description && test.description.toLowerCase().includes(searchTerm.toLowerCase())) // Added check for description existence
      );
  }, [tests, selectedCategory, searchTerm]);

  const handleOrderClick = (testId: string) => {
    // Navigate to the OrderTest page or trigger an order flow
    console.log('Order test:', testId); // Placeholder
    navigate(`/order-test?testId=${testId}`); // Example navigation
  };

  return (
    <div className="flex h-full bg-background text-foreground">
      {/* Sidebar for Categories */}
      <aside className="w-64 border-r border-border p-4 hidden md:block flex-shrink-0 overflow-y-auto bg-muted/30">
        <h2 className="text-lg font-semibold mb-4 text-primary">Categories</h2>
        {isLoading ? (
          <Loading text="Loading categories..." />
        ) : error ? (
          <p className="text-destructive text-sm">Error loading categories</p>
        ) : (
          <TestCategoryList 
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />
        )}
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 p-4 md:p-6 overflow-y-auto">
         <h1 className="text-2xl font-bold tracking-tight mb-4">Test Catalog</h1>
         <p className="text-muted-foreground mb-6">Browse available medical tests and learn more about them.</p>
         
        <div className="mb-6">
          <TestSearch searchTerm={searchTerm} onSearchChange={setSearchTerm} />
          {/* TODO: Add Mobile category select dropdown using Select from shadcn/ui */}
        </div>

        {isLoading ? (
          <Loading text="Loading tests..." />
        ) : error ? (
          <ErrorMessage message={error.message || 'Failed to load tests.'} />
        ) : filteredTests.length === 0 ? (
          <EmptyState 
            title="No Tests Found"
            message={searchTerm || selectedCategory 
              ? "No tests match your current search or category selection."
              : "No tests are available in the catalog at this time."
            }
            // illustration={Microscope} // Example Icon
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTests.map(test => (
              <TestDetailCard 
                key={test.id} 
                test={test} 
                onOrderClick={handleOrderClick} // Pass order handler
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default TestCatalog; 